import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { Actions, Mutations } from "@/store/enums/StoreEnums";

// Interfaccia per i dati grezzi del comune
export interface ComuneRaw {
  codice_istat: string;
  denominazione_ita_altra: string;
  denominazione_ita: string;
  denominazione_altra: string;
  cap: string;
  sigla_provincia: string;
  denominazione_provincia: string;
  tipologia_provincia: string;
  codice_regione: string;
  denominazione_regione: string;
  tipologia_regione: string;
  ripartizione_geografica: string;
  flag_capoluogo: string;
  codice_belfiore: string;
  lat: string;
  lon: string;
  superficie_kmq: string;
}

// Interfacce per i dati preprocessati
export interface Comune {
  codice_istat: string;
  denominazione: string;
  cap: string;
  sigla_provincia: string;
  codice_belfiore: string;
  lat: number;
  lon: number;
  flag_capoluogo: boolean;
}

export interface Provincia {
  sigla: string;
  denominazione: string;
  tipologia: string;
  codice_regione: string;
}

export interface Regione {
  codice: string;
  denominazione: string;
  tipologia: string;
  ripartizione_geografica: string;
}

// Struttura dati preprocessata per accessi veloci
export interface GeoData {
  comuni: Map<string, Comune>; // chiave: denominazione lowercase
  comuniPerCap: Map<string, Comune[]>; // chiave: cap
  comuniPerProvincia: Map<string, Comune[]>; // chiave: sigla_provincia
  comuniPerRegione: Map<string, Comune[]>; // chiave: denominazione_regione lowercase
  province: Map<string, Provincia>; // chiave: denominazione lowercase
  provincePerRegione: Map<string, Provincia[]>; // chiave: denominazione_regione lowercase
  regioni: Map<string, Regione>; // chiave: denominazione lowercase
}

@Module
export default class GeoModule extends VuexModule {
  private comuni: Map<string, Comune> = new Map();
  private comuniPerCap: Map<string, Comune[]> = new Map();
  private comuniPerProvincia: Map<string, Comune[]> = new Map();
  private comuniPerRegione: Map<string, Comune[]> = new Map();
  private province: Map<string, Provincia> = new Map();
  private provincePerRegione: Map<string, Provincia[]> = new Map();
  private regioni: Map<string, Regione> = new Map();

  private isLoaded = false;

  /**
   * Verifica se i dati sono stati caricati
   */
  get dataLoaded(): boolean {
    return this.isLoaded;
  }

  /**
   * Ottiene tutti i comuni
   */
  get allComuni(): Comune[] {
    return Array.from(this.comuni.values());
  }

  /**
   * Ottiene un comune per denominazione (case-insensitive)
   */
  get getComuneByDenominazione() {
    return (denominazione: string): Comune | undefined => {
      return this.comuni.get(denominazione.toLowerCase());
    };
  }

  /**
   * Ottiene un comune per codice ISTAT
   */
  get getComuneByCodiceIstat() {
    return (codiceIstat: string): Comune | undefined => {
      return this.allComuni.find((c) => c.codice_istat === codiceIstat);
    };
  }

  /**
   * Ottiene i comuni per CAP
   */
  get getComuniByCAP() {
    return (cap: string): Comune[] => {
      return this.comuniPerCap.get(cap) || [];
    };
  }

  /**
   * Ottiene i comuni per provincia
   */
  get getComuniByProvincia() {
    return (siglaProvincia: string): Comune[] => {
      return this.comuniPerProvincia.get(siglaProvincia.toUpperCase()) || [];
    };
  }

  /**
   * Ottiene i comuni per regione (case-insensitive)
   */
  get getComuniByRegione() {
    return (denominazioneRegione: string): Comune[] => {
      return (
        this.comuniPerRegione.get(denominazioneRegione.toLowerCase()) || []
      );
    };
  }

  /**
   * Ottiene tutte le province
   */
  get allProvince(): Provincia[] {
    return Array.from(this.province.values());
  }

  /**
   * Ottiene una provincia per denominazione (case-insensitive)
   */
  get getProvinciaByDenominazione() {
    return (denominazione: string): Provincia | undefined => {
      return this.province.get(denominazione.toLowerCase());
    };
  }

  /**
   * Ottiene una provincia per sigla
   */
  get getProvinciaBySigla() {
    return (sigla: string): Provincia | undefined => {
      return this.allProvince.find(
        (p) => p.sigla.toUpperCase() === sigla.toUpperCase()
      );
    };
  }

  /**
   * Ottiene le province per regione (case-insensitive)
   */
  get getProvinceByRegione() {
    return (denominazioneRegione: string): Provincia[] => {
      return (
        this.provincePerRegione.get(denominazioneRegione.toLowerCase()) || []
      );
    };
  }

  /**
   * Ottiene tutte le regioni
   */
  get allRegioni(): Regione[] {
    return Array.from(this.regioni.values());
  }

  /**
   * Ottiene una regione per denominazione (case-insensitive)
   */
  get getRegioneByDenominazione() {
    return (denominazione: string): Regione | undefined => {
      return this.regioni.get(denominazione.toLowerCase());
    };
  }

  /**
   * Ottiene una regione per codice
   */
  get getRegioneByCodice() {
    return (codice: string): Regione | undefined => {
      return this.allRegioni.find((r) => r.codice === codice);
    };
  }

  /**
   * Cerca comuni per nome (ricerca parziale case-insensitive)
   * Ricerca ottimizzata sulle chiavi della Map
   */
  get searchComuni() {
    return (query: string): Comune[] => {
      const lowerQuery = query.toLowerCase();
      const results: Comune[] = [];

      // Itera sulle chiavi (già lowercase) per performance
      Array.from(this.comuni.entries()).forEach(([key, comune]) => {
        if (key.includes(lowerQuery)) {
          results.push(comune);
        }
      });

      return results;
    };
  }

  /**
   * Cerca province per nome (ricerca parziale case-insensitive)
   * Ricerca ottimizzata sulle chiavi della Map
   */
  get searchProvince() {
    return (query: string): Provincia[] => {
      const lowerQuery = query.toLowerCase();
      const results: Provincia[] = [];

      // Itera sulle chiavi (già lowercase) per performance
      Array.from(this.province.entries()).forEach(([key, provincia]) => {
        if (key.includes(lowerQuery)) {
          results.push(provincia);
        }
      });

      return results;
    };
  }

  /**
   * Cerca regioni per nome (ricerca parziale case-insensitive)
   * Ricerca ottimizzata sulle chiavi della Map
   */
  get searchRegioni() {
    return (query: string): Regione[] => {
      const lowerQuery = query.toLowerCase();
      const results: Regione[] = [];

      // Itera sulle chiavi (già lowercase) per performance
      Array.from(this.regioni.entries()).forEach(([key, regione]) => {
        if (key.includes(lowerQuery)) {
          results.push(regione);
        }
      });

      return results;
    };
  }

  /**
   * Ricerca globale per sottostringa (cerca in comuni, province e regioni)
   * Restituisce risultati raggruppati per tipo
   * Ricerca ottimizzata sulle chiavi delle Map
   */
  get searchAll() {
    return (
      query: string
    ): {
      comuni: Comune[];
      province: Provincia[];
      regioni: Regione[];
    } => {
      const lowerQuery = query.toLowerCase();
      const comuni: Comune[] = [];
      const province: Provincia[] = [];
      const regioni: Regione[] = [];

      // Ricerca ottimizzata sulle chiavi
      Array.from(this.comuni.entries()).forEach(([key, comune]) => {
        if (key.includes(lowerQuery)) {
          comuni.push(comune);
        }
      });

      Array.from(this.province.entries()).forEach(([key, provincia]) => {
        if (key.includes(lowerQuery)) {
          province.push(provincia);
        }
      });

      Array.from(this.regioni.entries()).forEach(([key, regione]) => {
        if (key.includes(lowerQuery)) {
          regioni.push(regione);
        }
      });

      return { comuni, province, regioni };
    };
  }

  /**
   * Ottiene tutti i CAP disponibili
   */
  get allCAP(): string[] {
    return Array.from(this.comuniPerCap.keys()).sort();
  }

  @Mutation
  [Mutations.SET_GEO_DATA](geoData: GeoData) {
    this.comuni = geoData.comuni;
    this.comuniPerCap = geoData.comuniPerCap;
    this.comuniPerProvincia = geoData.comuniPerProvincia;
    this.comuniPerRegione = geoData.comuniPerRegione;
    this.province = geoData.province;
    this.provincePerRegione = geoData.provincePerRegione;
    this.regioni = geoData.regioni;
    this.isLoaded = true;
  }

  @Action
  [Actions.LOAD_GEO_DATA]() {
    // Usa this.context.state per accedere allo state nel modulo non dinamico
    const state = this.context.state as GeoModule;

    if (state.isLoaded) {
      return; // Dati già caricati
    }

    try {
      console.log("🚀 Inizio caricamento dati geografici...");

      // Importa il JSON preprocessato
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const processedData = require("@/data/geo-data-processed.json");

      console.log("✅ JSON caricato:", {
        comuni: Object.keys(processedData.comuni || {}).length,
        province: Object.keys(processedData.province || {}).length,
        regioni: Object.keys(processedData.regioni || {}).length,
      });

      // Converte gli oggetti JSON in Map
      const geoData: GeoData = {
        comuni: new Map(Object.entries(processedData.comuni || {})),
        comuniPerCap: new Map(
          Object.entries(processedData.comuniPerCap || {}).map(([k, v]) => [
            k,
            v as Comune[],
          ])
        ),
        comuniPerProvincia: new Map(
          Object.entries(processedData.comuniPerProvincia || {}).map(
            ([k, v]) => [k, v as Comune[]]
          )
        ),
        comuniPerRegione: new Map(
          Object.entries(processedData.comuniPerRegione || {}).map(([k, v]) => [
            k,
            v as Comune[],
          ])
        ),
        province: new Map(Object.entries(processedData.province || {})),
        provincePerRegione: new Map(
          Object.entries(processedData.provincePerRegione || {}).map(
            ([k, v]) => [k, v as Provincia[]]
          )
        ),
        regioni: new Map(Object.entries(processedData.regioni || {})),
      };

      this.context.commit(Mutations.SET_GEO_DATA, geoData);
      console.log("✅ Dati geografici caricati con successo!");
    } catch (error) {
      console.error("❌ Errore nel caricamento dei dati geografici:", error);
      console.error("Stack trace:", error);
      throw error;
    }
  }
}
